import { matchJobIcon } from './jobTypes'
import { offerTotal, type TyreOffer } from './tyreOffers'
import type {
  TyreFitment,
  TyreSizeOption,
  WidgetTyreSelectionRequest,
} from '../types/PublicBookingChannelTypes'

/**
 * Tyre size handling.
 *
 * Sizes come from the vehicle lookup where UKVD knows them, but a customer with
 * an unusual car, aftermarket wheels or a plate we can't resolve has to be able
 * to type what's on the sidewall. Both paths end up in the same shape.
 */

export interface TyreSizeChoice {
  /** Normalised sidewall code, e.g. `205/55R16`. */
  size: string
  /**
   * How the size was arrived at, which is worth telling the garage: a size the
   * garage holds for the car, a factory fitment from the registration, or typed.
   */
  source: 'vehicle' | 'fitment' | 'manual'
  front: boolean
  rear: boolean
}

/** The rear axle of a staggered fitment, or a different rear size typed in. */
export interface TyreRearChoice {
  /** Normalised sidewall code, e.g. `255/35R18`. */
  size: string
  /** As printed, e.g. `255/35 R18 94Y`, when it came from a fitment. */
  label?: string | null
  /** Rear tyres to fit, 0 to {@link MAX_AXLE_QUANTITY}. */
  quantity: number
}

export interface TyreSelection {
  /** The front axle, or every wheel when there is no `rear`. */
  size: TyreSizeChoice
  /**
   * Tyres for `size`: 1 to 4 for one size all round, or 0 to
   * {@link MAX_AXLE_QUANTITY} for the front of a staggered car.
   */
  quantity: number
  /** Set when the rear axle takes a different size. */
  rear?: TyreRearChoice | null
  /** The factory fitment is run-flat, so run-flats are fitted to match. */
  runFlat?: boolean
  /** The front size as printed, e.g. `225/45 R17 91W`, when it came from a fitment. */
  label?: string | null
}

/**
 * Parses a sidewall size.
 *
 * Accepts the many ways people write it — `205/55R16`, `205/55 R16`,
 * `205 55 16`, `205/55ZR16 91V` — and normalises to `205/55R16`. Returns null
 * for anything that isn't a plausible car tyre, so the customer gets told
 * rather than the garage receiving nonsense.
 */
export function parseTyreSize(input: string): string | null {
  const cleaned = input.trim().toUpperCase().replace(/\s+/g, ' ')
  if (!cleaned) return null

  // width / aspect [construction letters] rim [C for commercial, load/speed index]
  // The trailing (?!\d) rather than \b matters: van tyres are written
  // `225/65R16C`, and a word boundary refuses to match before the C.
  const match = /^(\d{3})\s*[/\- ]\s*(\d{2})\s*(?:[A-Z]{0,2}R?)?\s*[/\- ]?\s*(\d{2})(?!\d)/.exec(
    cleaned
  )
  if (!match) return null

  const width = Number(match[1])
  const aspect = Number(match[2])
  const rim = Number(match[3])

  // Bounds sanity: outside these it is not a car tyre, and a typo is far more
  // likely than a genuine 90-section 8-inch wheel.
  if (width < 125 || width > 355) return null
  if (aspect < 25 || aspect > 85) return null
  if (rim < 10 || rim > 24) return null

  return `${width}/${aspect}R${rim}`
}

/** Human-readable position label for a size. */
export function describePosition(size: TyreSizeChoice): string {
  if (size.front && size.rear) return 'All four wheels'
  if (size.front) return 'Front wheels'
  if (size.rear) return 'Rear wheels'
  return 'Any wheel'
}

/** Turns the API's tyre sizes into choices the tyre step can render. */
export function toSizeChoices(sizes: TyreSizeOption[] | null | undefined): TyreSizeChoice[] {
  if (!sizes?.length) return []

  return sizes
    .map((option): TyreSizeChoice | null => {
      const normalised = parseTyreSize(option.size ?? '')
      if (!normalised) return null

      return {
        size: normalised,
        source: 'vehicle',
        front: Boolean(option.front),
        rear: Boolean(option.rear),
      }
    })
    .filter((choice): choice is TyreSizeChoice => choice !== null)
}

/**
 * Work that plainly does not involve buying a tyre, so the size is not needed.
 *
 * Tested before {@link SUPPLY_PATTERN} because several of these do mention
 * fitting or replacing something: a puncture repair refits the same tyre, and a
 * valve replacement replaces a valve.
 */
const NO_SIZE_PATTERN =
  /\bpuncture|\brepair|\bpressure|\brotat|\bbalanc|\balign|\btracking\b|\btpms\b|\bvalve|\bnut\b|\binspect|\bcheck\b|\bnitrogen\b|\bdisposal\b/

/** Wording that means new rubber is being supplied, which needs a size. */
const SUPPLY_PATTERN = /\bfit|\bsuppl|\bnew\b|\breplac|\bchange\b|\bswap\b|\bpart[-\s]?worn\b/

/** What a single piece of text says about needing a size, if anything. */
function classify(text: string | null | undefined): 'supply' | 'no-size' | null {
  const value = (text ?? '').trim().toLowerCase()
  if (!value) return null

  if (NO_SIZE_PATTERN.test(value)) return 'no-size'
  if (SUPPLY_PATTERN.test(value)) return 'supply'
  return null
}

/**
 * Whether a service needs the customer to give a tyre size.
 *
 * Only tyre supply does. A puncture repair, pressure check or rotation happens on
 * the tyres already on the car, and asking a customer to read a sidewall for work
 * that does not depend on it is a screen they should never see.
 *
 * The name decides, and the description is only consulted when the name says
 * nothing either way. That order matters: a real fitting service is described as
 * "fit, balance, new valve and old tyre disposal", and reading the two together
 * would let the valve and the disposal argue it out of needing a size.
 *
 * A tyre service that says nothing either way counts as supply — a category of
 * "Tyres" holding a service called "Tyres" means fitting them.
 */
export function needsTyreSize(service: {
  name?: string | null
  description?: string | null
}): boolean {
  const verdict = classify(service.name) ?? classify(service.description)
  if (verdict) return verdict === 'supply'

  // Nothing decisive anywhere. Callers only ask about services already in the
  // tyre category, where supply is the norm — but a service with no text at all
  // is not evidence of anything.
  return Boolean(`${service.name ?? ''}${service.description ?? ''}`.trim())
}

/** The parts of a service this decision reads. */
export interface TyreSizeCandidate {
  category?: string | null
  name?: string | null
  description?: string | null
}

/**
 * Whether a booking made of these services has to ask for a tyre size.
 *
 * True only when one of them is tyre supply. Everything else — a puncture repair,
 * an alignment, a service and MOT — leaves the size step out of the journey
 * entirely.
 *
 * A service counts as being about tyres by its category or its own name, so
 * fitting still triggers the question when a garage files it under
 * "Wheels & Tyres" or sells it as "4 new tyres" under something else.
 */
export function tyreSizeRequired(chosen: TyreSizeCandidate[]): boolean {
  return chosen.some(
    (service) =>
      (matchJobIcon(service.category ?? '') === 'tyres' ||
        /\btyre|\btire/i.test(service.name ?? '')) &&
      needsTyreSize(service)
  )
}

/** Largest sensible order. Four corners is the practical maximum for a booking. */
export const MAX_TYRE_QUANTITY = 4

/** One axle of a staggered car: two wheels. */
export const MAX_AXLE_QUANTITY = 2

export function clampQuantity(quantity: number): number {
  if (!Number.isFinite(quantity)) return 1
  return Math.min(MAX_TYRE_QUANTITY, Math.max(1, Math.round(quantity)))
}

/**
 * The note handed to the garage alongside the booking.
 *
 * Tyre supply is not priced online — there is no stock or supplier-pricing
 * integration yet — so the size and count have to travel with the job for the
 * garage to quote against.
 */
export function describeTyreSelection(selection: TyreSelection): string {
  // Where the size came from, so the garage knows whether to double-check it. It
  // must not read as "the customer is bringing the tyres" — a workshop reading
  // that would not order any.
  const source =
    selection.size.source === 'manual' ? ' (size given by customer)' : ' (size from registration)'

  if (selection.rear) {
    const { rear } = selection
    const axles = [
      selection.quantity > 0 ? `${selection.quantity} × ${selection.size.size} front` : null,
      rear.quantity > 0 ? `${rear.quantity} × ${rear.size} rear` : null,
    ].filter(Boolean)
    return `${axles.join(', ')}${source}`
  }

  const count = clampQuantity(selection.quantity)
  const plural = count === 1 ? 'tyre' : 'tyres'
  return `${count} × ${selection.size.size} ${plural}${source}`
}

/**
 * The basket's tyre fitting service, by the same rule as the server: in a tyre
 * category, the garage's primary one first, otherwise the first in the basket.
 * Once a tyre is priced fully fitted, this line is included in that price.
 */
export function tyreFittingServiceId(
  services: Array<{ id: string; category?: string | null; presentationRole?: string | null }>,
  basketIds: string[]
): string | null {
  const tyreServices = basketIds
    .map((id) => services.find((service) => service.id === id))
    .filter((service): service is (typeof services)[number] => service != null)
    .filter((service) => /tyre|tire/i.test(service.category ?? ''))

  return (
    tyreServices.find((service) => service.presentationRole === 'primary') ?? tyreServices[0]
  )?.id ?? null
}

/** Whether the front and rear axles take different sizes. */
export function isStaggered(selection: TyreSelection | null | undefined): boolean {
  return Boolean(selection?.rear)
}

/** Every tyre being fitted, across both axles. */
export function totalTyres(selection: TyreSelection): number {
  return selection.rear ? selection.quantity + selection.rear.quantity : selection.quantity
}

/**
 * A fitment as the customer reads it: "225/45 R17 91W (standard)", or for a
 * staggered car "Front 225/40 R18 92Y · Rear 255/35 R18 94Y".
 */
export function fitmentLabel(fitment: TyreFitment): string {
  const front = fitment.front.label?.trim() || fitment.front.size
  const rear = fitment.rear ? fitment.rear.label?.trim() || fitment.rear.size : null
  const base = rear && !sameSize(fitment) ? `Front ${front} · Rear ${rear}` : front
  return fitment.standard ? `${base} (standard)` : base
}

function sameSize(fitment: TyreFitment): boolean {
  if (!fitment.rear) return true
  return parseTyreSize(fitment.rear.size) === parseTyreSize(fitment.front.size)
}

/** Whether a fitment has a different rear size. */
export function isStaggeredFitment(fitment: TyreFitment): boolean {
  return Boolean(fitment.rear) && !sameSize(fitment)
}

/**
 * The fitments worth offering, standard first and otherwise in the order the
 * lookup gave them. A fitment whose front size cannot be read is dropped rather
 * than shown as a choice that cannot be booked.
 */
export function orderFitments(fitments: TyreFitment[] | null | undefined): TyreFitment[] {
  if (!fitments?.length) return []

  const seen = new Set<string>()
  const usable = fitments.filter((fitment) => {
    if (!fitment?.front || !parseTyreSize(fitment.front.size ?? '')) return false
    if (fitment.rear && !parseTyreSize(fitment.rear.size ?? '')) return false
    const key = fitmentKey(fitment)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return [
    ...usable.filter((fitment) => fitment.standard),
    ...usable.filter((fitment) => !fitment.standard),
  ]
}

/** Identifies a fitment by what would be fitted, so a selection can be matched back to it. */
export function fitmentKey(fitment: TyreFitment): string {
  const front = parseTyreSize(fitment.front.size) ?? fitment.front.size
  const rear = isStaggeredFitment(fitment) && fitment.rear ? parseTyreSize(fitment.rear.size) : ''
  return `${front}|${rear ?? ''}|${fitment.runFlat ? 'rf' : ''}`
}

/** The same key for a selection, or null when it did not come from a fitment. */
export function selectionFitmentKey(selection: TyreSelection | null): string | null {
  if (!selection || selection.size.source !== 'fitment') return null
  return `${selection.size.size}|${selection.rear?.size ?? ''}|${selection.runFlat ? 'rf' : ''}`
}

/**
 * Choosing a fitment. A staggered car starts at a pair on each axle, and one
 * size all round at a full set of four; the customer changes either.
 */
export function selectionFromFitment(fitment: TyreFitment): TyreSelection | null {
  const front = parseTyreSize(fitment.front.size)
  if (!front) return null

  const staggered = isStaggeredFitment(fitment)
  const rear = staggered && fitment.rear ? parseTyreSize(fitment.rear.size) : null
  if (staggered && !rear) return null

  return {
    size: { size: front, source: 'fitment', front: true, rear: !staggered },
    quantity: staggered ? MAX_AXLE_QUANTITY : MAX_TYRE_QUANTITY,
    rear:
      staggered && rear && fitment.rear
        ? { size: rear, label: fitment.rear.label ?? null, quantity: MAX_AXLE_QUANTITY }
        : null,
    runFlat: fitment.runFlat === true,
    label: fitment.front.label ?? null,
  }
}

/**
 * The booking's `tyreSelection`. Offer ids are only sent with a product
 * chosen for every axle being fitted. The server prices every offer again; the
 * totals the customer was shown go with their offer ids (ACA-98) so a price
 * that moved since is refused rather than charged.
 */
export function toTyreSelectionRequest(
  selection: TyreSelection,
  offers: {
    frontOfferId?: string | null
    rearOfferId?: string | null
    frontShownTotal?: number | null
    rearShownTotal?: number | null
  } = {}
): WidgetTyreSelectionRequest {
  if (selection.rear) {
    const frontOfferId = selection.quantity > 0 ? offers.frontOfferId ?? null : null
    const rearOfferId = selection.rear.quantity > 0 ? offers.rearOfferId ?? null : null
    return {
      frontSize: selection.size.size,
      frontQuantity: selection.quantity,
      frontOfferId,
      rearSize: selection.rear.size,
      rearQuantity: selection.rear.quantity,
      rearOfferId,
      runFlat: selection.runFlat === true,
      ...shownTotal('frontShownTotal', frontOfferId, offers.frontShownTotal),
      ...shownTotal('rearShownTotal', rearOfferId, offers.rearShownTotal),
    }
  }

  const frontOfferId = offers.frontOfferId ?? null
  return {
    frontSize: selection.size.size,
    frontQuantity: clampQuantity(selection.quantity),
    frontOfferId,
    rearSize: null,
    rearQuantity: null,
    rearOfferId: null,
    runFlat: selection.runFlat === true,
    ...shownTotal('frontShownTotal', frontOfferId, offers.frontShownTotal),
  }
}

/** A shown total only rides with its offer id; without one there is nothing to check. */
function shownTotal(
  key: 'frontShownTotal' | 'rearShownTotal',
  offerId: string | null,
  total: number | null | undefined
): Partial<WidgetTyreSelectionRequest> {
  if (!offerId || total == null || !Number.isFinite(total)) return {}
  return { [key]: total }
}

/**
 * The axle totals the customer is shown for the chosen tyres (ACA-98): each
 * offer's total for its axle's quantity, before any quantity offer, which the
 * server does not know about. Null for an axle with no tyre chosen.
 */
export function shownTyreTotals(
  selection: TyreSelection,
  front: TyreOffer | null,
  rear: TyreOffer | null
): { frontShownTotal: number | null; rearShownTotal: number | null } {
  const frontQuantity = selection.rear ? selection.quantity : clampQuantity(selection.quantity)
  const rearQuantity = selection.rear?.quantity ?? 0
  return {
    frontShownTotal: front && frontQuantity > 0 ? offerTotal(front, frontQuantity) : null,
    rearShownTotal:
      selection.rear && rear && rearQuantity > 0 ? offerTotal(rear, rearQuantity) : null,
  }
}
